/**
 * Official OneSignal Web v16 pattern (same as dashboard snippet):
 *   window.OneSignalDeferred = window.OneSignalDeferred || [];
 *   OneSignalDeferred.push(async function (OneSignal) { await OneSignal.init({ appId }); });
 *   <script src="https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js" defer></script>
 *
 * We register the callback first, then inject that script so the queue is processed correctly.
 * Do not add the same script tag in index.html or the SDK may load before our callback runs.
 */

const PAGE_SDK_URL = 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';

let initPromise = null;
/** Resolved OneSignal API from the deferred callback (same as window.OneSignal after init). */
let oneSignalApi = null;

function getAppId() {
  return process.env.REACT_APP_ONESIGNAL_APP_ID?.trim() || '';
}

/** Must match OneSignal dashboard “Local Testing” — treat localhost / 127.0.0.1 as secure for Web Push. */
function allowLocalhostAsSecureOrigin() {
  if (typeof window === 'undefined') return process.env.NODE_ENV === 'development';
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || process.env.NODE_ENV === 'development';
}

export function getOneSignal() {
  return oneSignalApi ?? window.OneSignal ?? null;
}

/**
 * @returns {Promise<object|null>} Resolves with the OneSignal API object, or null if no app id.
 */
export function ensureOneSignalInitialized() {
  const appId = getAppId();
  if (!appId) {
    return Promise.resolve(null);
  }

  if (!initPromise) {
    initPromise = new Promise((resolve, reject) => {
      window.OneSignalDeferred = window.OneSignalDeferred || [];

      window.OneSignalDeferred.push(async function (OneSignal) {
        try {
          if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
            const site = window.location.origin;
            if (site && !site.includes('localhost') && !site.includes('127.0.0.1')) {
              console.info('[OneSignal] Dashboard Site URL should match this origin:', site);
            }
            if (window.location.hostname === '127.0.0.1') {
              console.warn(
                '[OneSignal] Use http://localhost:3000 (not 127.0.0.1) so it matches the typical Site URL in the dashboard.'
              );
            }
          }

          await OneSignal.init({
            appId,
            allowLocalhostAsSecureOrigin: allowLocalhostAsSecureOrigin(),
            serviceWorkerPath: '/OneSignalSDKWorker.js',
          });
          oneSignalApi = OneSignal;
          resolve(OneSignal);
        } catch (err) {
          reject(err);
        }
      });

      // Never return early: if the script tag already exists, our push still runs when the SDK flushes the queue.
      if (!document.querySelector(`script[src="${PAGE_SDK_URL}"]`)) {
        const s = document.createElement('script');
        s.src = PAGE_SDK_URL;
        s.defer = true;
        s.onerror = () => reject(new Error('Failed to load OneSignal SDK from CDN'));
        document.head.appendChild(s);
      }
    });
  }

  return initPromise;
}
